import { useEffect, useState } from "react";

import { SharedItemClient } from "@/Common/Api/SharedItem/SharedItemClient";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { Modal } from "@/Common/Components/Modals/Modal";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { SharedItemNote } from "@/Common/Models/Portal/SharedItem/SharedItemNote";
import { PortalCommunicationNotesList } from "../../../Common/Components/SharedItem/PortalCommunicationNotesList";

import styles from "./PortalCommunicationNotesPopup.module.scss";

interface IPortalCommunicationNotesPopupProps {
    sharedItemId: string;
    sharedItemNotes: SharedItemNote[];
    leftPosition: number;
    topPosition: number;
    footerPosition: number;
    onSaveNote: (sharedItemId: string) => void;
    onCancel: () => void;
}

export function PortalCommunicationNotesPopup(props: Readonly<IPortalCommunicationNotesPopupProps>): JSX.Element {
    const { getText } = useTranslation();

    const [note, setNote] = useState<string>("");
    const [popupTopPosition, setPopupTopPosition] = useState<number>(props.topPosition);

    const notePopupId = "portalCommunicationSharedItemNotesPopup";

    useEffect(() => {
        const popupElement = $(`#${notePopupId}`);
        const popupBottomPosition = props.topPosition + popupElement.height();

        if (popupBottomPosition > props.footerPosition) {
            setPopupTopPosition(props.topPosition - popupBottomPosition + props.footerPosition);
        }
    }, [props.footerPosition, props.topPosition, props.sharedItemNotes]);

    const handleSaveSharedItemNote = async (): Promise<void> => {
        const response = await SharedItemClient.saveSharedItemNote(props.sharedItemId, note);
        if (response) {
            setNote("");
            props.onSaveNote(props.sharedItemId);
        }
    };

    return (
        <Modal
            id={notePopupId}
            title={getText("NotesLabel")}
            top={popupTopPosition}
            left={props.leftPosition}
            maxHeight="400px"
            onClose={() => props.onCancel()}
            footer={(
                <div className={styles.actionButtonContainer}>
                    <ButtonControl
                        className="btn btn-default push-right-5"
                        onClick={() => props.onCancel()}
                        label={getText("CancelLabel")}
                        dataCy="portal-communication-notes-popup-cancel-button"
                    />
                    <ButtonControl
                        className="btn btn-primary"
                        onClick={() => handleSaveSharedItemNote()}
                        label={getText("SaveLabel")}
                        IsDisabled={!note}
                        dataCy="portal-communication-notes-popup-save-button"
                    />
                </div>
            )}
            width={600}
            roundTopCorner
            draggable
            dataCy="portal-communication-notes-popup"
        >
            {!!props.sharedItemNotes.length && (
                <PortalCommunicationNotesList
                    notes={props.sharedItemNotes}
                    isPopup
                />
            )}
            <textarea
                className={`${styles.textBoxContainer} form-control`}
                value={note}
                id="note-textbox-portal-communication"
                data-cy="portal-communication-notes-popup-textbox"
                onChange={e => setNote(e.target.value)}
                maxLength={255}
                rows={4}
                autoFocus
            />
        </Modal>
    );
}
